import { useState, useRef, useCallback } from 'react';
import { printContent } from '../../lib/exportUtils';
import { downloadPdfBlob } from '../../lib/downloadPdfBlob';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { 
  Download, 
  Printer, 
  Palette, 
  Image as ImageIcon,
  Loader2,
  X,
  Upload,
  Check,
  LayoutTemplate,
  Sparkles,
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Inject print styles once — hides everything except invoice when window.print() is called
const PRINT_STYLE_ID = 'countbucks-invoice-print-styles';
if (typeof document !== 'undefined' && !document.getElementById(PRINT_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = PRINT_STYLE_ID;
  style.textContent = `
    @media print {
      body > *:not(#invoice-print-root) { display: none !important; }
      #invoice-print-root { display: block !important; position: static !important; }
      .no-print, .print\\:hidden { display: none !important; }
      @page { margin: 1.5cm; size: A4; }
    }
  `;
  document.head.appendChild(style);
}

// Color presets
const COLOR_PRESETS = [
  { id: 'slate', hex: '#334155', name: 'Charcoal' },
  { id: 'blue', hex: '#2563eb', name: 'Blue' },
  { id: 'indigo', hex: '#4f46e5', name: 'Indigo' },
  { id: 'emerald', hex: '#059669', name: 'Emerald' },
  { id: 'teal', hex: '#0d9488', name: 'Teal' },
  { id: 'purple', hex: '#7c3aed', name: 'Purple' },
  { id: 'rose', hex: '#e11d48', name: 'Rose' },
  { id: 'amber', hex: '#b45309', name: 'Amber' },
];

// Template definitions
const TEMPLATES = {
  modern: { id: 'modern', name: 'Modern', description: 'Clean lines, minimal, bold header' },
  classic: { id: 'classic', name: 'Classic', description: 'Traditional, bordered, formal' },
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2,
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Lighten a hex color
function lighten(hex, amount = 0.92) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.round((num >> 16) + (255 - (num >> 16)) * amount);
  const g = Math.round(((num >> 8) & 0x00FF) + (255 - ((num >> 8) & 0x00FF)) * amount);
  const b = Math.round((num & 0x0000FF) + (255 - (num & 0x0000FF)) * amount);
  return `rgb(${r}, ${g}, ${b})`;
}

// ============ MODERN TEMPLATE ============
const ModernInvoice = ({ invoice, companyInfo, logo, color, subtotal, tax, total }) => (
  <div className="bg-white text-gray-900 overflow-hidden" style={{ fontFamily: "'Inter', -apple-system, sans-serif" }}>
    {/* Accent bar */}
    <div className="h-1.5" style={{ backgroundColor: color }} />
    
    {/* Header */}
    <div className="px-10 pt-8 pb-6 flex justify-between items-start">
      <div className="flex items-center gap-4">
        {logo ? (
          <img src={logo} alt="Logo" className="h-12 w-auto object-contain" />
        ) : (
          <div className="w-11 h-11 rounded-lg flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: color }}>
            {(companyInfo.name || 'C')[0]}
          </div>
        )}
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">{companyInfo.name || 'Your Company'}</h1>
          {companyInfo.tagline && <p className="text-xs text-gray-400 mt-0.5">{companyInfo.tagline}</p>}
        </div>
      </div>
      <div className="text-right">
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gray-400">Invoice</p>
        <p className="text-2xl font-bold tracking-tight mt-0.5" style={{ color }}>#{invoice?.invoice_no || '—'}</p>
      </div>
    </div>
    
    {/* Info row */}
    <div className="mx-10 px-6 py-5 rounded-xl grid grid-cols-2 gap-8" style={{ backgroundColor: lighten(color) }}>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-2" style={{ color }}>From</p>
        <p className="text-sm font-semibold text-gray-900">{companyInfo.name || 'Your Company'}</p>
        {companyInfo.address && <p className="text-xs text-gray-500 mt-1">{companyInfo.address}</p>}
        {companyInfo.email && <p className="text-xs text-gray-500">{companyInfo.email}</p>}
        {companyInfo.phone && <p className="text-xs text-gray-500">{companyInfo.phone}</p>}
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-2" style={{ color }}>Bill To</p>
        <p className="text-sm font-semibold text-gray-900">{invoice?.customer_name || 'Customer'}</p>
        {invoice?.customer_address && <p className="text-xs text-gray-500 mt-1">{invoice.customer_address}</p>}
        {invoice?.customer_email && <p className="text-xs text-gray-500">{invoice.customer_email}</p>}
      </div>
    </div>
    
    {/* Dates */}
    <div className="px-10 py-4 flex gap-8 text-xs">
      <div>
        <span className="text-gray-400">Issue Date: </span>
        <span className="font-medium text-gray-700">{formatDateShort(invoice?.invoice_date || invoice?.date)}</span>
      </div>
      <div>
        <span className="text-gray-400">Due Date: </span>
        <span className="font-medium text-gray-700">{formatDateShort(invoice?.due_date)}</span>
      </div>
      {invoice?.reference && (
        <div>
          <span className="text-gray-400">Ref: </span>
          <span className="font-medium text-gray-700">{invoice.reference}</span>
        </div>
      )}
    </div>
    
    {/* Line items */}
    <div className="px-10 pb-6">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2" style={{ borderColor: color }}>
            <th className="text-left py-2.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-500">Description</th>
            <th className="text-center py-2.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-500 w-16">Qty</th>
            <th className="text-right py-2.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-500 w-24">Rate</th>
            <th className="text-right py-2.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-500 w-20">Tax</th>
            <th className="text-right py-2.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-500 w-28">Amount</th>
          </tr>
        </thead>
        <tbody>
          {(invoice?.line_items || []).map((line, idx) => (
            <tr key={idx} className="border-b border-gray-100">
              <td className="py-3 pr-4">
                <p className="font-medium text-gray-900">{line.description}</p>
                {line.account_name && <p className="text-[11px] text-gray-400 mt-0.5">{line.account_name}</p>}
              </td>
              <td className="py-3 text-center text-gray-600">{line.quantity}</td>
              <td className="py-3 text-right text-gray-600">{formatCurrency(line.unit_price)}</td>
              <td className="py-3 text-right text-gray-400">{line.tax_rate || 0}%</td>
              <td className="py-3 text-right font-medium text-gray-900">{formatCurrency((line.quantity || 0) * (line.unit_price || 0))}</td>
            </tr>
          ))}
          {(!invoice?.line_items || invoice.line_items.length === 0) && (
            <tr><td colSpan={5} className="py-8 text-center text-gray-300 text-xs">No line items</td></tr>
          )}
        </tbody>
      </table>
    </div>
    
    {/* Totals */}
    <div className="px-10 pb-6 flex justify-end">
      <div className="w-64">
        <div className="flex justify-between py-2 text-sm text-gray-500">
          <span>Subtotal</span>
          <span className="text-gray-700">{formatCurrency(invoice?.subtotal || subtotal)}</span>
        </div>
        <div className="flex justify-between py-2 text-sm text-gray-500 border-b border-gray-100">
          <span>Tax</span>
          <span className="text-gray-700">{formatCurrency(invoice?.tax || tax)}</span>
        </div>
        <div className="flex justify-between py-3 mt-1 rounded-lg px-4 text-white font-bold" style={{ backgroundColor: color }}>
          <span>Total Due</span>
          <span>{formatCurrency(invoice?.total || total)}</span>
        </div>
        {invoice?.amount_paid > 0 && (
          <>
            <div className="flex justify-between py-2 text-sm mt-1">
              <span className="text-gray-500">Paid</span>
              <span className="text-emerald-600 font-medium">-{formatCurrency(invoice.amount_paid)}</span>
            </div>
            <div className="flex justify-between py-2 text-sm font-semibold border-t">
              <span>Balance Due</span>
              <span style={{ color }}>{formatCurrency(invoice.balance_due)}</span>
            </div>
          </>
        )}
      </div>
    </div>
    
    {/* Notes */}
    {invoice?.memo && (
      <div className="px-10 pb-6">
        <div className="p-4 rounded-lg border border-gray-100">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] mb-1.5" style={{ color }}>Notes</p>
          <p className="text-xs text-gray-500 leading-relaxed">{invoice.memo}</p>
        </div>
      </div>
    )}
    
    {/* Footer */}
    <div className="px-10 py-4 border-t border-gray-100 text-center">
      <p className="text-xs text-gray-400">Thank you for your business</p>
      {companyInfo.website && <p className="text-[10px] text-gray-300 mt-0.5">{companyInfo.website}</p>}
    </div>
  </div>
);

// ============ CLASSIC TEMPLATE ============
const ClassicInvoice = ({ invoice, companyInfo, logo, color, subtotal, tax, total }) => (
  <div className="bg-white text-gray-900 overflow-hidden" style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
    {/* Header */}
    <div className="px-10 pt-10 pb-6 flex justify-between items-start border-b-2" style={{ borderColor: color }}>
      <div className="flex items-center gap-5">
        {logo ? (
          <img src={logo} alt="Logo" className="h-14 w-auto object-contain" />
        ) : (
          <div className="w-14 h-14 rounded border-2 flex items-center justify-center" style={{ borderColor: color, color }}>
            <span className="text-2xl font-bold">{(companyInfo.name || 'C')[0]}</span>
          </div>
        )}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{companyInfo.name || 'Your Company'}</h1>
          {companyInfo.tagline && <p className="text-xs text-gray-400 italic mt-1">{companyInfo.tagline}</p>}
          <div className="text-xs text-gray-500 mt-2 space-y-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>
            {companyInfo.address && <p>{companyInfo.address}</p>}
            <p>{[companyInfo.phone, companyInfo.email].filter(Boolean).join(' | ')}</p>
          </div>
        </div>
      </div>
      <div className="text-right">
        <h2 className="text-3xl font-bold tracking-[0.15em] uppercase" style={{ color }}>Invoice</h2>
        <p className="text-lg font-semibold text-gray-600 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>#{invoice?.invoice_no || '—'}</p>
      </div>
    </div>
    
    {/* Bill To + Dates */}
    <div className="px-10 py-6 grid grid-cols-5 gap-6">
      <div className="col-span-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2 border-b pb-1" style={{ color, borderColor: color, fontFamily: "'Inter', sans-serif" }}>Bill To</p>
        <p className="text-sm font-bold text-gray-900 mt-2">{invoice?.customer_name || 'Customer'}</p>
        {invoice?.customer_address && <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>{invoice.customer_address}</p>}
        {invoice?.customer_email && <p className="text-xs text-gray-500" style={{ fontFamily: "'Inter', sans-serif" }}>{invoice.customer_email}</p>}
      </div>
      <div className="col-span-2">
        <div className="border rounded-lg overflow-hidden" style={{ borderColor: color + '40' }}>
          <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-white" style={{ backgroundColor: color, fontFamily: "'Inter', sans-serif" }}>
            Invoice Details
          </div>
          <div className="divide-y" style={{ divideColor: color + '20' }}>
            <div className="px-4 py-2 flex justify-between text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
              <span className="text-gray-500">Date</span>
              <span className="font-medium">{formatDateShort(invoice?.invoice_date || invoice?.date)}</span>
            </div>
            <div className="px-4 py-2 flex justify-between text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
              <span className="text-gray-500">Due Date</span>
              <span className="font-medium">{formatDateShort(invoice?.due_date)}</span>
            </div>
            {invoice?.reference && (
              <div className="px-4 py-2 flex justify-between text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
                <span className="text-gray-500">Reference</span>
                <span className="font-medium">{invoice.reference}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    
    {/* Line items */}
    <div className="px-10 pb-6">
      <table className="w-full text-sm border-collapse" style={{ fontFamily: "'Inter', sans-serif" }}>
        <thead>
          <tr className="text-white text-[10px] uppercase tracking-[0.15em]" style={{ backgroundColor: color }}>
            <th className="text-left py-2.5 px-4 font-semibold">Description</th>
            <th className="text-center py-2.5 px-3 font-semibold w-16">Qty</th>
            <th className="text-right py-2.5 px-3 font-semibold w-24">Unit Price</th>
            <th className="text-right py-2.5 px-3 font-semibold w-16">Tax</th>
            <th className="text-right py-2.5 px-4 font-semibold w-28">Amount</th>
          </tr>
        </thead>
        <tbody>
          {(invoice?.line_items || []).map((line, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50/60' : 'bg-white'}>
              <td className="py-2.5 px-4 border-b border-gray-100">
                <p className="font-medium text-gray-800">{line.description}</p>
                {line.account_name && <p className="text-[10px] text-gray-400 mt-0.5">{line.account_name}</p>}
              </td>
              <td className="py-2.5 px-3 text-center text-gray-600 border-b border-gray-100">{line.quantity}</td>
              <td className="py-2.5 px-3 text-right text-gray-600 border-b border-gray-100">{formatCurrency(line.unit_price)}</td>
              <td className="py-2.5 px-3 text-right text-gray-400 border-b border-gray-100">{line.tax_rate || 0}%</td>
              <td className="py-2.5 px-4 text-right font-medium text-gray-900 border-b border-gray-100">{formatCurrency((line.quantity || 0) * (line.unit_price || 0))}</td>
            </tr>
          ))}
          {(!invoice?.line_items || invoice.line_items.length === 0) && (
            <tr><td colSpan={5} className="py-8 text-center text-gray-300 text-xs italic">No line items</td></tr>
          )}
        </tbody>
      </table>
    </div>
    
    {/* Totals */}
    <div className="px-10 pb-8 flex justify-end" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="w-72 border rounded-lg overflow-hidden" style={{ borderColor: color + '40' }}>
        <div className="flex justify-between px-4 py-2.5 text-sm border-b" style={{ borderColor: color + '20' }}>
          <span className="text-gray-500">Subtotal</span>
          <span className="font-medium text-gray-700">{formatCurrency(invoice?.subtotal || subtotal)}</span>
        </div>
        <div className="flex justify-between px-4 py-2.5 text-sm border-b" style={{ borderColor: color + '20' }}>
          <span className="text-gray-500">Tax</span>
          <span className="font-medium text-gray-700">{formatCurrency(invoice?.tax || tax)}</span>
        </div>
        <div className="flex justify-between px-4 py-3 text-base font-bold text-white" style={{ backgroundColor: color }}>
          <span>Total</span>
          <span>{formatCurrency(invoice?.total || total)}</span>
        </div>
        {invoice?.amount_paid > 0 && (
          <>
            <div className="flex justify-between px-4 py-2 text-sm border-b" style={{ borderColor: color + '20' }}>
              <span className="text-gray-500">Paid</span>
              <span className="font-medium text-emerald-600">-{formatCurrency(invoice.amount_paid)}</span>
            </div>
            <div className="flex justify-between px-4 py-2.5 text-sm font-bold" style={{ color }}>
              <span>Balance Due</span>
              <span>{formatCurrency(invoice.balance_due)}</span>
            </div>
          </>
        )}
      </div>
    </div>
    
    {/* Notes */}
    {invoice?.memo && (
      <div className="px-10 pb-6">
        <div className="p-4 border-l-4 bg-gray-50 rounded-r-lg" style={{ borderColor: color }}>
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] mb-1" style={{ color, fontFamily: "'Inter', sans-serif" }}>Notes</p>
          <p className="text-xs text-gray-500 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{invoice.memo}</p>
        </div>
      </div>
    )}
    
    {/* Footer */}
    <div className="px-10 py-5 border-t-2 text-center" style={{ borderColor: color }}>
      <p className="text-xs italic text-gray-500">Thank you for your business</p>
      {companyInfo.website && <p className="text-[10px] text-gray-400 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>{companyInfo.website}</p>}
    </div>
  </div>
);


// ============ MAIN COMPONENT ============
export function InvoicePreview({ 
  invoice, 
  companyInfo = {},
  theme = 'blue',
  customColor = null,
  logo = null,
  defaultTaxRate = 0,
  onThemeChange,
  onLogoChange,
  onCustomColorChange,
  onDefaultTaxRateChange,
  showCustomization = true,
  className 
}) {
  const fileInputRef = useRef(null);
  const [template, setTemplate] = useState('modern');
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  
  // Resolve color
  const activeColor = customColor || (COLOR_PRESETS.find(c => c.id === theme) || COLOR_PRESETS[0]).hex;

  const subtotal = invoice?.line_items?.reduce((sum, line) => sum + ((line.quantity || 0) * (line.unit_price || 0)), 0) || 0;
  const tax = invoice?.line_items?.reduce((sum, line) => sum + ((line.quantity || 0) * (line.unit_price || 0) * (line.tax_rate || 0) / 100), 0) || 0;
  const total = subtotal + tax;

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    processFile(file);
  };

  const processFile = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Logo must be less than 2MB'); return; }
    const reader = new FileReader();
    reader.onload = (e) => onLogoChange?.(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) processFile(file);
  }, []);

  // Generate print HTML for both templates
  const generatePrintHTML = () => {
    const color = activeColor;
    const light = lighten(color);
    const isModern = template === 'modern';
    
    const lineItemsHTML = (invoice?.line_items || []).map((line, idx) => `
      <tr${!isModern && idx % 2 === 0 ? ' style="background:#f9fafb"' : ''}>
        <td style="padding:10px 14px;border-bottom:1px solid #f3f4f6">${line.description || ''}</td>
        <td style="padding:10px;text-align:center;border-bottom:1px solid #f3f4f6">${line.quantity || 0}</td>
        <td style="padding:10px;text-align:right;border-bottom:1px solid #f3f4f6">${formatCurrency(line.unit_price)}</td>
        <td style="padding:10px;text-align:right;border-bottom:1px solid #f3f4f6;color:#9ca3af">${line.tax_rate || 0}%</td>
        <td style="padding:10px 14px;text-align:right;font-weight:500;border-bottom:1px solid #f3f4f6">${formatCurrency((line.quantity || 0) * (line.unit_price || 0))}</td>
      </tr>
    `).join('');
    
    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Invoice #${invoice?.invoice_no || ''}</title>
<style>
  @page { size: letter; margin: 0.5in; }
  * { margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important; }
  body { font-family: ${isModern ? "'Inter',-apple-system,sans-serif" : "'Georgia','Times New Roman',serif"}; font-size:12px; line-height:1.5; color:#1f2937; background:white; }
  .sans { font-family: 'Inter',-apple-system,sans-serif; }
  table { width:100%;border-collapse:collapse; }
  th { background:${color};color:white;padding:10px 14px;font-size:10px;text-transform:uppercase;letter-spacing:1.5px;font-weight:600;font-family:'Inter',sans-serif; }
  th:first-child { text-align:left; } th:last-child { text-align:right; }
  th:nth-child(2),th:nth-child(4) { text-align:center; } th:nth-child(3) { text-align:right; }
</style></head><body>
${isModern ? `
  <div style="height:6px;background:${color}"></div>
  <div style="padding:32px 40px 24px;display:flex;justify-content:space-between;align-items:flex-start">
    <div style="display:flex;align-items:center;gap:16px">
      ${logo ? `<img src="${logo}" style="height:48px;width:auto" />` : `<div style="width:44px;height:44px;border-radius:8px;background:${color};color:white;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:18px;font-family:Inter,sans-serif">${(companyInfo.name || 'C')[0]}</div>`}
      <div>
        <div style="font-size:18px;font-weight:600;font-family:Inter,sans-serif">${companyInfo.name || 'Your Company'}</div>
        ${companyInfo.tagline ? `<div style="font-size:10px;color:#9ca3af;margin-top:2px;font-family:Inter,sans-serif">${companyInfo.tagline}</div>` : ''}
      </div>
    </div>
    <div style="text-align:right">
      <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:3px;color:#9ca3af;font-family:Inter,sans-serif">Invoice</div>
      <div style="font-size:22px;font-weight:700;color:${color};font-family:Inter,sans-serif">#${invoice?.invoice_no || '—'}</div>
    </div>
  </div>
  <div style="margin:0 40px;padding:20px 24px;border-radius:12px;background:${light};display:grid;grid-template-columns:1fr 1fr;gap:32px;font-family:Inter,sans-serif">
    <div>
      <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:2px;color:${color};margin-bottom:8px">From</div>
      <div style="font-weight:600;font-size:13px">${companyInfo.name || 'Your Company'}</div>
      ${companyInfo.address ? `<div style="font-size:11px;color:#6b7280;margin-top:4px">${companyInfo.address}</div>` : ''}
      ${companyInfo.email ? `<div style="font-size:11px;color:#6b7280">${companyInfo.email}</div>` : ''}
      ${companyInfo.phone ? `<div style="font-size:11px;color:#6b7280">${companyInfo.phone}</div>` : ''}
    </div>
    <div>
      <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:2px;color:${color};margin-bottom:8px">Bill To</div>
      <div style="font-weight:600;font-size:13px">${invoice?.customer_name || 'Customer'}</div>
      ${invoice?.customer_address ? `<div style="font-size:11px;color:#6b7280;margin-top:4px">${invoice.customer_address}</div>` : ''}
      ${invoice?.customer_email ? `<div style="font-size:11px;color:#6b7280">${invoice.customer_email}</div>` : ''}
    </div>
  </div>
` : `
  <div style="padding:40px 40px 24px;display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid ${color}">
    <div style="display:flex;align-items:center;gap:20px">
      ${logo ? `<img src="${logo}" style="height:56px;width:auto" />` : `<div style="width:56px;height:56px;border:3px solid ${color};border-radius:4px;display:flex;align-items:center;justify-content:center;font-size:24px;font-weight:bold;color:${color}">${(companyInfo.name || 'C')[0]}</div>`}
      <div>
        <div style="font-size:22px;font-weight:700">${companyInfo.name || 'Your Company'}</div>
        ${companyInfo.tagline ? `<div style="font-size:11px;color:#9ca3af;font-style:italic;margin-top:2px">${companyInfo.tagline}</div>` : ''}
        <div style="font-size:11px;color:#6b7280;margin-top:8px;font-family:Inter,sans-serif">
          ${companyInfo.address ? `${companyInfo.address}<br>` : ''}
          ${[companyInfo.phone, companyInfo.email].filter(Boolean).join(' | ')}
        </div>
      </div>
    </div>
    <div style="text-align:right">
      <div style="font-size:28px;font-weight:700;letter-spacing:4px;text-transform:uppercase;color:${color}">Invoice</div>
      <div style="font-size:16px;font-weight:600;color:#4b5563;margin-top:4px;font-family:Inter,sans-serif">#${invoice?.invoice_no || '—'}</div>
    </div>
  </div>
  <div style="padding:24px 40px;display:grid;grid-template-columns:3fr 2fr;gap:24px;font-family:Inter,sans-serif">
    <div>
      <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:${color};border-bottom:1px solid ${color};padding-bottom:4px;margin-bottom:8px">Bill To</div>
      <div style="font-weight:600;font-size:13px;margin-top:4px">${invoice?.customer_name || 'Customer'}</div>
      ${invoice?.customer_address ? `<div style="font-size:11px;color:#6b7280;margin-top:4px">${invoice.customer_address}</div>` : ''}
      ${invoice?.customer_email ? `<div style="font-size:11px;color:#6b7280">${invoice.customer_email}</div>` : ''}
    </div>
    <div>
      <div style="border:1px solid ${color}40;border-radius:8px;overflow:hidden">
        <div style="background:${color};color:white;padding:8px 14px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:1.5px">Invoice Details</div>
        <div style="padding:8px 14px;font-size:11px;display:flex;justify-content:space-between;border-bottom:1px solid #f3f4f6"><span style="color:#6b7280">Date</span><span style="font-weight:500">${formatDateShort(invoice?.invoice_date || invoice?.date)}</span></div>
        <div style="padding:8px 14px;font-size:11px;display:flex;justify-content:space-between"><span style="color:#6b7280">Due Date</span><span style="font-weight:500">${formatDateShort(invoice?.due_date)}</span></div>
        ${invoice?.reference ? `<div style="padding:8px 14px;font-size:11px;display:flex;justify-content:space-between;border-top:1px solid #f3f4f6"><span style="color:#6b7280">Ref</span><span style="font-weight:500">${invoice.reference}</span></div>` : ''}
      </div>
    </div>
  </div>
`}
  <div style="padding:${isModern ? '16px 40px 24px' : '0 40px 24px'}">
    <table><thead><tr><th>Description</th><th>Qty</th><th>Rate</th><th>Tax</th><th>Amount</th></tr></thead>
    <tbody>${lineItemsHTML}</tbody></table>
  </div>
  <div style="padding:0 40px 24px;display:flex;justify-content:flex-end">
    <div style="width:260px${!isModern ? ';border:1px solid ' + color + '40;border-radius:8px;overflow:hidden' : ''}">
      <div style="display:flex;justify-content:space-between;padding:8px ${isModern ? '0' : '14px'};font-size:12px;color:#6b7280;border-bottom:1px solid #f3f4f6"><span>Subtotal</span><span style="color:#374151">${formatCurrency(invoice?.subtotal || subtotal)}</span></div>
      <div style="display:flex;justify-content:space-between;padding:8px ${isModern ? '0' : '14px'};font-size:12px;color:#6b7280;border-bottom:1px solid #f3f4f6"><span>Tax</span><span style="color:#374151">${formatCurrency(invoice?.tax || tax)}</span></div>
      <div style="display:flex;justify-content:space-between;padding:12px ${isModern ? '16px' : '14px'};background:${color};color:white;font-weight:700;font-size:14px;${isModern ? 'border-radius:8px;margin-top:6px' : ''}"><span>Total</span><span>${formatCurrency(invoice?.total || total)}</span></div>
      ${invoice?.amount_paid > 0 ? `
      <div style="display:flex;justify-content:space-between;padding:8px ${isModern ? '0' : '14px'};font-size:12px;border-bottom:1px solid #f3f4f6"><span style="color:#6b7280">Paid</span><span style="color:#059669;font-weight:500">-${formatCurrency(invoice.amount_paid)}</span></div>
      <div style="display:flex;justify-content:space-between;padding:8px ${isModern ? '0' : '14px'};font-size:12px;font-weight:700"><span>Balance Due</span><span style="color:${color}">${formatCurrency(invoice.balance_due)}</span></div>
      ` : ''}
    </div>
  </div>
  ${invoice?.memo ? `<div style="padding:0 40px 24px"><div style="padding:12px 16px;${isModern ? 'border:1px solid #f3f4f6;border-radius:8px' : 'border-left:4px solid ' + color + ';background:#f9fafb;border-radius:0 8px 8px 0'}"><div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:1.5px;color:${color};margin-bottom:4px;font-family:Inter,sans-serif">Notes</div><div style="font-size:11px;color:#6b7280;font-family:Inter,sans-serif">${invoice.memo}</div></div></div>` : ''}
  <div style="padding:16px 40px;border-top:${isModern ? '1px solid #f3f4f6' : '3px solid ' + color};text-align:center">
    <div style="font-size:11px;color:#9ca3af;${!isModern ? 'font-style:italic' : ''}">${isModern ? 'Thank you for your business' : 'Thank you for your business'}</div>
    ${companyInfo.website ? `<div style="font-size:10px;color:#d1d5db;margin-top:2px;font-family:Inter,sans-serif">${companyInfo.website}</div>` : ''}
  </div>
</body></html>`;
  };

  const handlePrint = async () => {
    // Generate PDF then route through the print wrapper. We bias toward the
    // same flow as handleSavePdf so both paths work identically across
    // deployed + preview environments.
    const node = document.getElementById('invoice-preview');
    if (!node) {
      // Fallback to the legacy printContent flow if the node hasn't mounted.
      const htmlContent = generatePrintHTML();
      const styleMatch = htmlContent.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
      const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      const styles = styleMatch ? styleMatch.join('\n') : '';
      const body = bodyMatch ? bodyMatch[1] : htmlContent;
      printContent(`${styles}${body}`, {
        filename: `Invoice_${invoice?.invoice_no || 'Draft'}.pdf`,
      });
      return;
    }
    try {
      const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pageWidth) / canvas.width;
      let y = 0;
      while (y < imgHeight) {
        if (y > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -y, pageWidth, imgHeight);
        y += pageHeight;
      }
      const blob = pdf.output('blob');
      await downloadPdfBlob(blob, `Invoice_${invoice?.invoice_no || 'Draft'}.pdf`, { mode: 'print' });
    } catch (err) {
      console.error('Print failed:', err);
    }
  };

  const handleSavePdf = async () => {
    // Use html2canvas + jsPDF to capture the rendered invoice as-is, then
    // route the resulting Blob through the shared downloadPdfBlob() helper
    // which uses the /api/pdf/{id}?action=download wrapper for a real
    // OS-level save (works around Chrome PDF-viewer inline override and
    // the Emergent preview iframe sandbox).
    const node = document.getElementById('invoice-preview');
    if (!node) { handlePrint(); return; }

    try {
      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * pageWidth) / canvas.width;

      let y = 0;
      while (y < imgHeight) {
        if (y > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -y, imgWidth, imgHeight);
        y += pageHeight;
      }

      const filename = `Invoice_${invoice?.invoice_no || 'Draft'}.pdf`;
      const blob = pdf.output('blob');
      await downloadPdfBlob(blob, filename, { mode: 'download' });
    } catch (err) {
      console.error('PDF generation failed:', err);
      // Fallback to server-side print rendering
      handlePrint();
    }
  };

  const invoiceProps = { invoice, companyInfo, logo, color: activeColor, subtotal, tax, total };

  return (
    <div className={cn("flex gap-0", className)} data-testid="invoice-preview-container">
      {/* Invoice Document */}
      <div className="flex-1 min-w-0">
        {/* Action bar */}
        <div className="flex items-center justify-between mb-4 print:hidden">
          <div className="flex items-center gap-2">
            <LayoutTemplate className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">{TEMPLATES[template].name} Template</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="invoice-print-btn">
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Print
            </Button>
            <Button variant="outline" size="sm" onClick={handleSavePdf} data-testid="invoice-pdf-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Save PDF
            </Button>
          </div>
        </div>
        
        {/* Paper */}
        <div className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.08)] border border-gray-200 overflow-hidden" id="invoice-preview" data-testid="invoice-paper">
          {template === 'modern' ? <ModernInvoice {...invoiceProps} /> : <ClassicInvoice {...invoiceProps} />}
        </div>
      </div>
      
      {/* Customization Sidebar */}
      {showCustomization && (
        <div className="w-56 ml-5 flex-shrink-0 print:hidden" data-testid="invoice-customization-panel">
          <div className="sticky top-0 space-y-6">
            
            {/* Template Picker */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">Template</p>
              <div className="space-y-2">
                {Object.values(TEMPLATES).map(t => (
                  <button
                    key={t.id}
                    onClick={() => setTemplate(t.id)}
                    className={cn(
                      "w-full text-left px-3 py-2.5 rounded-lg border transition-all text-sm",
                      template === t.id
                        ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-600"
                        : "border-border hover:border-border"
                    )}
                    data-testid={`template-${t.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={cn("font-medium", template === t.id ? "text-emerald-700 dark:text-emerald-400" : "text-foreground")}>
                        {t.name}
                      </span>
                      {template === t.id && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{t.description}</p>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Color Picker */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">Accent Color</p>
              <div className="grid grid-cols-4 gap-2">
                {COLOR_PRESETS.map(c => (
                  <button
                    key={c.id}
                    onClick={() => { onCustomColorChange?.(null); onThemeChange?.(c.id); }}
                    className={cn(
                      "w-full aspect-square rounded-lg border-2 transition-all hover:scale-110",
                      (theme === c.id && !customColor) ? "border-white/10 dark:border-white scale-110 shadow-md" : "border-transparent"
                    )}
                    style={{ backgroundColor: c.hex }}
                    title={c.name}
                    data-testid={`color-${c.id}`}
                  />
                ))}
              </div>
              
              {/* Custom color */}
              <Popover open={colorPickerOpen} onOpenChange={setColorPickerOpen}>
                <PopoverTrigger asChild>
                  <button
                    className={cn(
                      "w-full mt-2 flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-all",
                      customColor
                        ? "border-white/10 dark:border-white bg-muted"
                        : "border-border hover:border-border"
                    )}
                    data-testid="custom-color-btn"
                  >
                    <div className="w-4 h-4 rounded-full border border-border" style={{ backgroundColor: customColor || activeColor }} />
                    <span className="text-muted-foreground">{customColor ? customColor : 'Custom...'}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-52" align="start">
                  <div className="space-y-3">
                    <Label className="text-xs font-medium">Custom Color</Label>
                    <div className="flex gap-2">
                      <Input type="color" value={customColor || activeColor}
                        onChange={(e) => onCustomColorChange?.(e.target.value)}
                        className="w-10 h-9 p-0.5 cursor-pointer" />
                      <Input value={customColor || activeColor}
                        onChange={(e) => onCustomColorChange?.(e.target.value)}
                        placeholder="#2563eb" className="flex-1 text-xs font-mono" />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1 text-xs"
                        onClick={() => { onCustomColorChange?.(null); setColorPickerOpen(false); }}>
                        Reset
                      </Button>
                      <Button size="sm" className="flex-1 text-xs" onClick={() => setColorPickerOpen(false)}>
                        Apply
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Logo Upload */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">Logo</p>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
              
              {logo ? (
                <div className="relative group">
                  <div className="p-3 rounded-lg border border-border bg-muted flex items-center justify-center">
                    <img src={logo} alt="Logo" className="max-h-12 w-auto" />
                  </div>
                  <button
                    onClick={() => onLogoChange?.(null)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid="remove-logo-btn"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div
                  className={cn(
                    "border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors",
                    dragOver
                      ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20"
                      : "border-border hover:border-border"
                  )}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  data-testid="logo-upload-zone"
                >
                  <Upload className="w-5 h-5 mx-auto text-muted-foreground/50 mb-1.5" />
                  <p className="text-[11px] text-muted-foreground">Drop logo here</p>
                  <p className="text-[10px] text-muted-foreground/50 mt-0.5">or click to browse</p>
                </div>
              )}
            </div>
            
            {/* Default Tax Rate */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">Default Tax Rate</p>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={defaultTaxRate || ''}
                  onChange={(e) => onDefaultTaxRateChange?.(parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className="h-9 text-sm font-mono"
                  data-testid="default-tax-rate-input"
                />
                <span className="text-sm text-muted-foreground font-medium">%</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5">Applied to new line items</p>
            </div>
            
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoicePreview;
